import { useEffect, useRef } from 'react'

/**
 * Noticing that a new version has been released, and reloading for it.
 *
 * The television runs a kiosk browser pointed at the app and left open. A
 * release goes out and the Pi carries on running whatever it loaded the last
 * time somebody reloaded it — for days, if nobody thinks to.
 *
 * Which is not a caching problem, though it looks like one. The assets are
 * already content-hashed, so a reload picks up a new build correctly and a
 * build tag on the URLs would fix nothing while re-downloading every unchanged
 * image on every release. The gap is simpler: nothing ever reloads.
 *
 * `/api/health` has always said which version is running — the deploy jobs use
 * it to confirm a release came up. Nothing in the client had ever asked.
 *
 * **Only ever called from the menu**, and that is the whole of the design. A
 * reload during a round abandons it, and the deploy that would trigger one
 * happens whenever a merge lands — quite possibly while she is halfway through
 * Rocky's. So a kiosk sitting on the menu picks a release up by itself, and one
 * left inside a game picks it up the moment she comes back out. Never on the
 * host display either: that is a television showing a room somebody set up, and
 * reloading it would drop the room.
 */

/** Long enough to be nearly free, short enough that a kiosk is never far behind. */
const ASK_EVERY_MS = 5 * 60 * 1000

/**
 * The version this page is running, as first reported to it.
 *
 * Module scope, so it survives leaving the menu and coming back — the question
 * is whether the server has changed since this *page* loaded, not since this
 * component mounted. A reload starts the module afresh, which is what stops
 * the new version deciding it is stale and reloading again for ever.
 */
let running: string | null = null

/** Only exported so a test can start from nothing. */
export function forgetRunningVersion(): void {
  running = null
}

async function versionNow(signal: AbortSignal): Promise<string | null> {
  try {
    const reply = await fetch('/api/health', { signal, cache: 'no-store' })

    if (!reply.ok) return null

    const body: unknown = await reply.json()

    return typeof body === 'object' && body !== null && 'version' in body
      ? ((body as { version?: unknown }).version as string) || null
      : null
  } catch {
    /*
     * Nothing at all on failure. The television has to survive the server
     * being briefly unreachable — a wifi blip mid-round — without doing
     * anything dramatic, and there is nothing here worth telling a child.
     */
    return null
  }
}

export function usePickUpReleases(reload: () => void = () => window.location.reload()): void {
  // Held in a ref so passing an inline function does not restart the timer on
  // every render, the same as the key handlers do.
  const latest = useRef(reload)

  useEffect(() => {
    latest.current = reload
  })

  useEffect(() => {
    const stop = new AbortController()

    // Reloading is not instant, and a second reload on top of the first would
    // be a page that never finishes loading.
    let reloading = false

    async function check(): Promise<void> {
      if (reloading) return

      const version = await versionNow(stop.signal)

      if (version === null) return

      if (running === null) {
        running = version
        return
      }

      if (version !== running) {
        reloading = true
        latest.current()
      }
    }

    void check()
    const timer = setInterval(() => void check(), ASK_EVERY_MS)

    return () => {
      stop.abort()
      window.clearInterval(timer)
    }
  }, [])
}
